import * as React from "react";
import { useMemo, useRef, useState } from "react";
import { URLS, useQueryParam } from "@/utils";
import { Combobox, Input, Spinner } from "@/components/ui";
import { debounce } from "lodash";
import { EmptyView } from "@/components/EmptyView";
import { PageTitle } from "@/components";
import { SuperPageAssignmentsCategoryList } from "./SuperPageAssignmentsCategoryList";
import { useSuperPageAssignmentVideoPageModal } from "./useSuperPageAssignmentVideoPageModal";
import { useNewCategories } from "@/utils/useNewCategories";
import { Category } from "@/services/bedrock";
import { useSuperPageAssignmentSearch } from "@/utils/useSuperPageAssignmentSearch";
import { SuperPageAssignmentStatus } from "./SuperPageAssignmentStatus";

const StatusTypes = ["Available", "Requested", "Completed"];

const statusMap: Record<string, "AVAILABLE" | "REQUESTED" | "COMPLETED"> = {
  Available: "AVAILABLE",
  Requested: "REQUESTED",
  Completed: "COMPLETED",
};

const UNASSIGNED_CATEGORY: Category = {
  id: -1,
  coachId: -1,
  type: "SUPER_PAGE",
  name: "Uncategorized",
};

const toDisplayedCategory = (category: Category): Category | null =>
  category.id === UNASSIGNED_CATEGORY.id ? null : category;

export const SuperPageAssignmentsList: React.FC = () => {
  const { modal, onAssignmentClick } = useSuperPageAssignmentVideoPageModal();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useQueryParam("status", undefined, StatusTypes);
  const { categories, isFetching: isFetchingCategories } = useNewCategories("SUPER_PAGE");
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

  const categoryFilterItems = useMemo(() => [UNASSIGNED_CATEGORY, ...categories], [categories]);

  const displayedCategories: (Category | null)[] = useMemo(() => {
    if (selectedCategories.length === 0) {
      return [...categories, null];
    }
    return selectedCategories.map(toDisplayedCategory);
  }, [categories, selectedCategories]);

  const delayedQuery = useRef(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500)
  ).current;
  const onChangeSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    delayedQuery(e.target.value);
  };

  const assignmentStatus = useMemo(() => {
    if (!selectedStatus) return undefined;
    return statusMap[selectedStatus];
  }, [selectedStatus]);

  const hasAnyContent = displayedCategories.length > 0;

  const { superPageAssignments: probeAssignments, isFetching: isFetchingProbe } = useSuperPageAssignmentSearch({
    search: searchTerm.trim() || undefined,
    status: assignmentStatus,
    page: 1,
    perPage: 1,
  });

  const showEmptyView = hasAnyContent && !isFetchingProbe && (probeAssignments?.content.length ?? 0) === 0;

  return (
    <>
      <div className="flex flex-col gap-6">
        <PageTitle
          title="Video Page Assignments"
          breadcrumbs={[{ name: "Library", link: URLS.player.videoPages }]}
          hideSeparator
        />
        <div className="flex flex-wrap items-center gap-4">
          <Input className="min-w-50 max-w-80" placeholder="Search..." onChange={onChangeSearchTerm} />
          <Combobox
            className="min-w-50 max-w-80"
            searchable
            placeholder="Filter by Category"
            items={categoryFilterItems}
            value={selectedCategories}
            itemToStringLabel={item => item.name}
            itemToLabel={item =>
              item.id === UNASSIGNED_CATEGORY.id ? <span className="italic">{item.name}</span> : item.name
            }
            onValueChange={value => setSelectedCategories(value)}
            multiple
            maxChips="auto"
          />
          <Combobox
            className="min-w-50 max-w-80"
            showClear
            placeholder="Filter by status"
            items={StatusTypes}
            value={selectedStatus}
            onValueChange={value => setSelectedStatus(value ?? undefined)}
            itemToIcon={status => {
              const mappedStatus = statusMap[status];
              if (!mappedStatus) return null;
              return <SuperPageAssignmentStatus status={mappedStatus} />;
            }}
          />
          {isFetchingCategories && <Spinner />}
        </div>

        {hasAnyContent && (
          <div className="flex flex-col gap-8">
            {displayedCategories.map(category => (
              <SuperPageAssignmentsCategoryList
                key={`assignment-category-${category?.id ?? "unassigned"}`}
                category={category}
                searchTerm={searchTerm}
                status={assignmentStatus}
                onAssignmentClick={onAssignmentClick}
              />
            ))}
          </div>
        )}

        {!isFetchingCategories && !hasAnyContent && <EmptyView text="No video page assignments match your filters." />}
        {showEmptyView && <EmptyView text="No video page assignments match your filters." />}
      </div>
      {modal}
    </>
  );
};
